import { useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { deleteCustomer, deleteEmployee } from "../api/deleteUserApi";
import {
  DELETE_SUCCESS,
  OWNS_MICROGRIDS,
  formatDeleteUserError,
  parseDeleteUserError,
} from "../domain/deleteUserError";

// The Delete flow for Customer Projects and Employees: which row the dialog
// is open for, the request on confirm, and the toast for its result. On
// success the page refetches through onSuccess, so the row is gone; on a
// failure nothing is refetched, so the row stays. The dialog closes either
// way and the toast carries the result (the 409 stays open until dismissed).
export default function useDeleteUser({ accessToken, userId, onSuccess }) {
  // The row the dialog is open for, or null when closed.
  const [target, setTarget] = useState(null);
  // "customer" or "employee": which endpoint the row goes to.
  const [kind, setKind] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Blocks a second confirm that lands before the disabled button
  // re-renders, and an open or close while the request is in flight.
  const inFlightRef = useRef(false);

  const openDelete = useCallback((row, rowKind) => {
    if (inFlightRef.current) return;
    setTarget(row);
    setKind(rowKind);
  }, []);

  const closeDelete = useCallback(() => {
    if (inFlightRef.current) return;
    setTarget(null);
    setKind(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!target || inFlightRef.current) return;
    inFlightRef.current = true;
    setIsDeleting(true);

    const send = kind === "customer" ? deleteCustomer : deleteEmployee;
    try {
      await send({ accessToken, userId, email: target.email });
      toast.success(DELETE_SUCCESS[kind]);
      onSuccess?.();
    } catch (err) {
      // An unexpected throw (not a DeleteUserFailure) shows "The delete failed."
      const failure = err?.failure ?? parseDeleteUserError({});
      // The kind only: never the body, a name or the email.
      console.error("[useDeleteUser] Delete failed:", failure.kind);
      const message = formatDeleteUserError(failure);
      if (failure.kind === OWNS_MICROGRIDS) {
        toast.error(message, { autoClose: false });
      } else {
        toast.error(message);
      }
    } finally {
      inFlightRef.current = false;
      setTarget(null);
      setKind(null);
      setIsDeleting(false);
    }
  }, [target, kind, accessToken, userId, onSuccess]);

  return { target, kind, isDeleting, openDelete, closeDelete, confirmDelete };
}
